import React, {
  Children,
  cloneElement,
  useState,
  useLayoutEffect,
  useEffect,
} from "react";
import { useFormContext } from "react-hook-form";
import { SwitchItemProps } from "@/components/SwitchItem";
import "./style.css";

type SwitchProps<InitialValue> = {
  name: string;
  defaultValue?: InitialValue | string;
  children?: React.ReactElement<SwitchItemProps<InitialValue>>[];
  className?: string;
  type?: string;
  onChange?(event: React.ChangeEvent<HTMLInputElement>): void;
};

// Switch
const Switch = <InitialValue,>({
  name,
  children,
  defaultValue = "",
  className,
  // type = 'primary',
  onChange,
}: SwitchProps<InitialValue>) => {
  const { register, setValue } = useFormContext() || {};

  const [rawValue, setRawValue] = useState<InitialValue | string>(defaultValue);
  const [leftLabel, rightLabel] =
    Children.map(children, (child) => {
      if (!child) {
        return null;
      }
      return cloneElement(child, {
        ...child.props,
        selected: child.props.value === rawValue,
      });
    }) || [];

  useLayoutEffect(() => {
    if (!leftLabel || !rightLabel) {
      return;
    }
    const isUnknownValue =
      rawValue !== leftLabel.props.value && rawValue !== rightLabel.props.value;

    if (isUnknownValue) {
      setRawValue(leftLabel.props.value);
    }
  }, [leftLabel, rawValue, rightLabel]);

  useLayoutEffect(() => {
    register?.(name);
  }, [register, name]);

  useEffect(() => {
    setValue?.(name, rawValue);
  }, [name, setValue, rawValue]);

  if (!leftLabel || !rightLabel) {
    return null;
  }

  return (
    <div className={`flex items-center ${className || ""}`}>
      {leftLabel}
      <div className="form-switch relative mr-3 inline-block w-[4.75rem] select-none align-middle leading-normal lg:w-12">
        <input
          id={name}
          name={name}
          type="checkbox"
          className="form-switch-checkbox absolute -z-1 opacity-0 "
          checked={rawValue === rightLabel.props.value}
          value={rawValue as never as string}
          onChange={(e) => {
            const newValue = e.target.checked
              ? rightLabel.props.value
              : leftLabel.props.value;
            setRawValue(newValue);
            onChange?.(e);
          }}
        />
        <label
          htmlFor={name}
          className="form-switch-label block h-11 cursor-pointer overflow-hidden rounded-full bg-tim-blue_disabled transition duration-200 lg:h-6"
        />
      </div>
      {rightLabel}
    </div>
  );
};

export default Switch;
